// Per-record change history (prototype `pages/platform/Audit.tsx` → `History`), shared by every
// module's detail pages. Reads `/audit-log?entityId=`; needs `platform.audit.read`.
import { dateTime, type Page, relTime, useApi, useAuth } from "@keel/core";

export interface AuditEntry {
  id: string;
  at: string;
  actor: string;
  actorName: string | null;
  action: string;
  entityType: string;
  entityId: string;
  entityLabel: string | null;
  changes: Record<string, unknown> | null;
  correlationId: string | null;
}

export const actorClass = (a: string) =>
  a.startsWith("api_key") ? "actor-api" : a.startsWith("integration") ? "actor-integration" : "";

const words = (k: string) =>
  k
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .toLowerCase();

/** Compact change history for one record, shown in document side panels. */
export function History({ entityId }: { entityId: string }) {
  const { can } = useAuth();
  const allowed = can("platform.audit.read");
  const { data } = useApi<Page<AuditEntry>>(allowed ? `/audit-log?entityId=${entityId}&pageSize=20` : null);
  if (!allowed)
    return (
      <p className="hint" style={{ margin: 0 }}>
        Audit access is needed to see history.
      </p>
    );
  if (!data) return null;
  if (!data.data.length)
    return (
      <p className="hint" style={{ margin: 0 }}>
        No changes recorded.
      </p>
    );
  return (
    <ol className="feed compact">
      {data.data.map((e) => {
        const status =
          e.changes && "status" in e.changes
            ? ((e.changes.status as { to?: string })?.to ?? e.changes.status)
            : null;
        return (
          <li key={e.id}>
            <span className={`feed-dot feed-${e.action === "created" ? "doc" : "stock"}`} />
            <span className="feed-text">
              <b className={actorClass(e.actor)}>{e.actorName ?? "System"}</b>{" "}
              {e.action === "updated" && status ? `set status to ${words(String(status))}` : e.action}
            </span>
            <time className="muted" title={dateTime(e.at)}>
              {relTime(e.at)}
            </time>
          </li>
        );
      })}
    </ol>
  );
}
